import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { getErrorMessage } from '../../lib/errors';
import ButtonNB from '../ui-nb/ButtonNB';

interface OTPVerificationProps {
  orderId: string;
  method?: 'otp_sms' | 'otp_whatsapp';
  onVerified?: () => void;
  onError?: (error: string) => void;
}

export default function OTPVerification({
  orderId,
  method = 'otp_sms',
  onVerified,
  onError,
}: OTPVerificationProps) {
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!otpSent || !expiresAt) return;

    const timer = setInterval(() => {
      const remaining = Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000));
      setCountdown(remaining);
      if (remaining === 0) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [otpSent, expiresAt]);

  const handleGenerateOTP = async () => {
    setIsLoading(true);
    setLocalError(null);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('generate-otp', {
        body: { order_id: orderId, method },
      });

      if (invokeError) {
        throw invokeError;
      }

      if (data?.expires_at) {
        setExpiresAt(new Date(data.expires_at));
      } else {
        // Default 15 min expiry
        setExpiresAt(new Date(Date.now() + 15 * 60 * 1000));
      }
      setOtpSent(true);
      setCountdown(15 * 60);
    } catch (err) {
      const msg = getErrorMessage(err) || 'Failed to generate OTP';
      setLocalError(msg);
      onError?.(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otpCode.length !== 6) {
      setLocalError('Please enter a 6-digit code');
      return;
    }

    setIsLoading(true);
    setLocalError(null);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('verify-otp', {
        body: { order_id: orderId, otp_code: otpCode },
      });

      if (invokeError) {
        throw invokeError;
      }

      if (data?.success) {
        setOtpVerified(true);
        onVerified?.();
      } else {
        throw new Error(data?.error || 'Verification failed');
      }
    } catch (err) {
      const msg = getErrorMessage(err) || 'Invalid or expired OTP';
      setLocalError(msg);
      setOtpCode('');
      onError?.(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setOtpCode('');
    await handleGenerateOTP();
  };

  const formatCountdown = () => {
    const mins = Math.floor(countdown / 60);
    const secs = countdown % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (otpVerified) {
    return (
      <div className="flex items-center gap-2 text-nb-black">
        <span className="text-lg">✓</span>
        <span className="font-medium">Code verified</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!otpSent ? (
        <ButtonNB
          variant="primary"
          size="md"
          onClick={handleGenerateOTP}
          loading={isLoading}
          className="w-full"
        >
          Generate OTP
        </ButtonNB>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-nb-text-secondary">
            A verification code has been sent to the client.
            {countdown > 0 && ` Expires in ${formatCountdown()}.`}
          </p>

          <input
            type="text"
            placeholder="6-digit code"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="w-full px-4 py-2 rounded-[2px] text-center text-lg tracking-widest bg-white border-2 border-black text-black focus:outline-none focus:border-nb-yellow"
            maxLength={6}
            disabled={countdown === 0}
          />

          <ButtonNB
            variant="primary"
            size="md"
            onClick={handleVerifyOTP}
            loading={isLoading}
            disabled={otpCode.length !== 6 || countdown === 0}
            className="w-full"
          >
            Verify code
          </ButtonNB>

          {countdown === 0 ? (
            <button
              type="button"
              onClick={handleResendOTP}
              className="w-full text-sm text-nb-black hover:underline"
            >
              Regenerate OTP
            </button>
          ) : (
            <button
              type="button"
              onClick={handleResendOTP}
              className="w-full text-sm text-nb-text-muted hover:text-black"
            >
              Regenerate code
            </button>
          )}
        </div>
      )}

      {localError && (
        <p className="text-sm text-nb-red">{localError}</p>
      )}
    </div>
  );
}